import { useEffect, useRef } from 'react';

async function waitForFonts(): Promise<void> {
  const fonts = document.fonts;

  if (!fonts?.ready) {
    return;
  }

  await fonts.ready.catch(() => undefined);
}

async function waitForCurrentImages(): Promise<void> {
  const images = Array.from(document.images).filter(
    (image) => image.loading !== 'lazy' && !image.complete
  );

  await Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.addEventListener('load', () => resolve(), { once: true });
          image.addEventListener('error', () => resolve(), { once: true });
        })
    )
  );
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function waitForInitialDocumentLayout(): Promise<void> {
  await Promise.all([waitForFonts(), waitForCurrentImages()]);
  await nextFrame();
  await nextFrame();
}

/**
 * Opens the print dialog once per page while `enabled` holds. The caller owns
 * the readiness rule; this hook only waits for the committed document to lay
 * out. Losing `enabled` (an unmount, or a data fetch that starts after the
 * first commit) cancels the pending print, and regaining it starts a new wait
 * from the current document, so late images are waited for too.
 */
export function usePrintPage(pageId: string, enabled: boolean): void {
  const printedPageId = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || printedPageId.current === pageId) {
      return;
    }

    let cancelled = false;

    void waitForInitialDocumentLayout().then(() => {
      if (cancelled || printedPageId.current === pageId) {
        return;
      }

      printedPageId.current = pageId;
      window.print();
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, pageId]);
}
